// Import model
import { Field } from '../models/Field';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { FieldOptions } from '../models/FieldOptions';

// Import dependency serializers (for complex types)
import { FieldOptionsSerializer } from './FieldOptionsSerializer';

// Import derived type serializers (for polymorphic deserialization)
import { AddressFieldSerializer } from './AddressFieldSerializer';
import { ArrayFieldSerializer } from './ArrayFieldSerializer';
import { BitwiseFlagFieldSerializer } from './BitwiseFlagFieldSerializer';
import { CalculatedFieldSerializer } from './CalculatedFieldSerializer';
import { CheckboxFieldSerializer } from './CheckboxFieldSerializer';
import { ChildrenFieldSerializer } from './ChildrenFieldSerializer';
import { DateFieldSerializer } from './DateFieldSerializer';
import { DateRangeFieldSerializer } from './DateRangeFieldSerializer';
import { DateTimeFieldSerializer } from './DateTimeFieldSerializer';
import { DictionaryFieldSerializer } from './DictionaryFieldSerializer';
import { EmailFieldSerializer } from './EmailFieldSerializer';
import { ExpressionFieldSerializer } from './ExpressionFieldSerializer';
import { FileFieldSerializer } from './FileFieldSerializer';
import { GenericFieldSerializer } from './GenericFieldSerializer';
import { HiddenFieldSerializer } from './HiddenFieldSerializer';
import { ImageFieldSerializer } from './ImageFieldSerializer';
import { LabelFieldSerializer } from './LabelFieldSerializer';
import { LocationDistanceFieldSerializer } from './LocationDistanceFieldSerializer';
import { LocationFieldSerializer } from './LocationFieldSerializer';
import { LookupFieldSerializer } from './LookupFieldSerializer';
import { MultiReferenceFieldSerializer } from './MultiReferenceFieldSerializer';
import { MultiSelectFieldSerializer } from './MultiSelectFieldSerializer';
import { NumberFieldSerializer } from './NumberFieldSerializer';
import { ObjectFieldSerializer } from './ObjectFieldSerializer';
import { PasswordFieldSerializer } from './PasswordFieldSerializer';
import { PhoneFieldSerializer } from './PhoneFieldSerializer';
import { PostalCodeFieldSerializer } from './PostalCodeFieldSerializer';
import { ReferenceFieldSerializer } from './ReferenceFieldSerializer';
import { RelatedObjectsFieldSerializer } from './RelatedObjectsFieldSerializer';
import { SelectFieldSerializer } from './SelectFieldSerializer';
import { TagsFieldSerializer } from './TagsFieldSerializer';
import { TextFieldSerializer } from './TextFieldSerializer';
import { TimeFieldSerializer } from './TimeFieldSerializer';
import { UrlFieldSerializer } from './UrlFieldSerializer';

/**
 * Serializer for Field - handles JSON serialization and deserialization with type validation
 */
export class FieldSerializer {
  /**
   * Deserializes a JSON string into a Field instance
   */
  static deserialize(json: string): Field {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Field instance
   */
  static fromObject(obj: any): Field {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Field');
    }

    // Handle polymorphic deserialization using discriminator
    const discriminator = obj['_t'];

    switch (discriminator) {
      case 'AddressField':
        return AddressFieldSerializer.fromObject(obj);
      case 'ArrayField':
        return ArrayFieldSerializer.fromObject(obj);
      case 'BitwiseFlagField':
        return BitwiseFlagFieldSerializer.fromObject(obj);
      case 'CalculatedField':
        return CalculatedFieldSerializer.fromObject(obj);
      case 'CheckboxField':
        return CheckboxFieldSerializer.fromObject(obj);
      case 'ChildrenField':
        return ChildrenFieldSerializer.fromObject(obj);
      case 'DateField':
        return DateFieldSerializer.fromObject(obj);
      case 'DateRangeField':
        return DateRangeFieldSerializer.fromObject(obj);
      case 'DateTimeField':
        return DateTimeFieldSerializer.fromObject(obj);
      case 'DictionaryField':
        return DictionaryFieldSerializer.fromObject(obj);
      case 'EmailField':
        return EmailFieldSerializer.fromObject(obj);
      case 'ExpressionField':
        return ExpressionFieldSerializer.fromObject(obj);
      case 'FileField':
        return FileFieldSerializer.fromObject(obj);
      case 'GenericField':
        return GenericFieldSerializer.fromObject(obj);
      case 'HiddenField':
        return HiddenFieldSerializer.fromObject(obj);
      case 'ImageField':
        return ImageFieldSerializer.fromObject(obj);
      case 'LabelField':
        return LabelFieldSerializer.fromObject(obj);
      case 'LocationDistanceField':
        return LocationDistanceFieldSerializer.fromObject(obj);
      case 'LocationField':
        return LocationFieldSerializer.fromObject(obj);
      case 'LookupField':
        return LookupFieldSerializer.fromObject(obj);
      case 'MultiReferenceField':
        return MultiReferenceFieldSerializer.fromObject(obj);
      case 'MultiSelectField':
        return MultiSelectFieldSerializer.fromObject(obj);
      case 'NumberField':
        return NumberFieldSerializer.fromObject(obj);
      case 'ObjectField':
        return ObjectFieldSerializer.fromObject(obj);
      case 'PasswordField':
        return PasswordFieldSerializer.fromObject(obj);
      case 'PhoneField':
        return PhoneFieldSerializer.fromObject(obj);
      case 'PostalCodeField':
        return PostalCodeFieldSerializer.fromObject(obj);
      case 'ReferenceField':
        return ReferenceFieldSerializer.fromObject(obj);
      case 'RelatedObjectsField':
        return RelatedObjectsFieldSerializer.fromObject(obj);
      case 'SelectField':
        return SelectFieldSerializer.fromObject(obj);
      case 'TagsField':
        return TagsFieldSerializer.fromObject(obj);
      case 'TextField':
        return TextFieldSerializer.fromObject(obj);
      case 'TimeField':
        return TimeFieldSerializer.fromObject(obj);
      case 'UrlField':
        return UrlFieldSerializer.fromObject(obj);
      default:
        // Fall through to base type deserialization
        break;
    }

    const instance: Field = {
      t: obj['_t'] as string,
      description: obj['Description'] as string | undefined,
      apiName: obj['ApiName'] as string | undefined,
      defaultValue: obj['DefaultValue'] as any | undefined,
      isRequired: obj['IsRequired'] as boolean | undefined,
      options: obj['Options'] ? FieldOptionsSerializer.fromObject(obj['Options']) : undefined as any,
      name: obj['Name'] as string,
      label: obj['Label'] as string | undefined,
      enable: obj['Enable'] ? (obj['Enable'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      visible: obj['Visible'] ? (obj['Visible'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Field instances
   */
  static deserializeArray(json: string): Field[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Field instance to a JSON string
   */
  static serialize(instance: Field): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Field instance to a plain object
   */
  static toObject(instance: Field): any {
    return {
      '_t': instance.t,
      'Description': instance.description,
      'ApiName': instance.apiName,
      'DefaultValue': instance.defaultValue,
      'IsRequired': instance.isRequired,
      'Options': instance.options ? FieldOptionsSerializer.toObject(instance.options) : undefined,
      'Name': instance.name,
      'Label': instance.label,
      'Enable': instance.enable?.map((item: any) =>
        item
      ),
      'Visible': instance.visible?.map((item: any) =>
        item
      ),
    };
  }

  /**
   * Serializes an array of Field instances to a JSON string
   */
  static serializeArray(instances: Field[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
